/**
 * High-level verification used by the app's verifier screen.
 *
 * It takes the public bundle the API publishes (GET /api/v1/campaigns/{id}/verify)
 * and recomputes the result locally. Nothing in here trusts the server's own
 * verdict — the point is to derive one independently and compare.
 */
import { bytesToHex, hexToBytes } from './bytes';
import { ALGO_V1, drawV1, drawContext, ticketRoot, verifyCommit } from './v1';
import { drawWinnersV0, hashHexV0 } from './v0';
import { ALGO_V2, drawContextV2, drawV2, multiplierV2, ticketRootV2 } from './v2';

/** The shape of GET /api/v1/campaigns/{id}/verify. */
export interface VerifyBundle {
  campaign_id: string;
  campaign_name: string;
  recipe: string;
  algo: string;
  commit: string;
  server_seed?: string;
  verifiable: boolean;
  ticket_root?: string;
  ticket_count: number;
  winner_count: number;
  /** Вклад участников: сумма выбранных цифр и выведенный из неё множитель. */
  pick_sum?: number;
  multiplier?: number;
  external_source?: string;
  external_value?: string;
  context_hex?: string;
  winners?: Array<{
    position: number;
    ticket_number: string;
    ticket_sequence: number;
    region?: string;
    prize_label?: string;
    /** Цифра участника и судьба места — в v2 они часть публичного результата. */
    pick?: number;
    kind?: string;
    status?: string;
  }>;
  label: string;
  legacy_algo: boolean;
  explain: string;
}

export interface DrawCheck {
  /** True when the independently computed winners match the published ones. */
  matches: boolean;
  /** Whether the revealed seed matches the pre-published commitment. */
  commitOk: boolean;
  computed: Array<{ position: number; sequence: number }>;
  published: Array<{ position: number; sequence: number; number: string }>;
  /** Mismatched positions, for showing exactly where the two disagree. */
  mismatches: number[];
  contextHex: string;
  notes: string[];
}

/**
 * Recomputes a published draw from the bundle.
 *
 * `externalSeedOverride` lets a participant paste the public value from its
 * original source — the lottery's own site — rather than trusting the copy we
 * display. That is the whole point of an external seed, so the verifier has to
 * accept it as an input.
 */
export async function checkDraw(
  bundle: VerifyBundle,
  externalSeedOverride?: string,
): Promise<DrawCheck> {
  const notes: string[] = [];
  const published = (bundle.winners ?? []).map((w) => ({
    position: w.position,
    sequence: w.ticket_sequence,
    number: w.ticket_number,
  }));

  if (!bundle.verifiable || !bundle.server_seed) {
    throw new Error('Сид сервера ещё не раскрыт — пересчитать результат можно будет после розыгрыша.');
  }
  if (!bundle.ticket_root) {
    throw new Error('Список билетов не зафиксирован — нечего пересчитывать.');
  }

  const seed = hexToBytes(bundle.server_seed);
  const commitOk = await verifyCommit(seed, bundle.commit);
  if (!commitOk) {
    notes.push('Раскрытый сид НЕ соответствует хешу, опубликованному до старта акции.');
  }

  const external = (externalSeedOverride ?? bundle.external_value ?? '').trim();
  if (externalSeedOverride !== undefined && externalSeedOverride.trim() !== (bundle.external_value ?? '').trim()) {
    notes.push('Публичный сид отличается от опубликованного — пересчёт идёт по введённому значению.');
  }

  const v2 = bundle.algo === ALGO_V2;

  // В v2 вместе с победителями разыгрываются запасные, и пересчитывать надо
  // ровно столько позиций, сколько опубликовано: иначе списки не сойдутся.
  const winnerCount = v2
    ? Math.max(Math.min(bundle.winner_count, bundle.ticket_count), published.length)
    : Math.min(bundle.winner_count, bundle.ticket_count);

  const input = {
    campaignId: bundle.campaign_id,
    externalSeed: external,
    ticketRoot: bundle.ticket_root,
    ticketCount: bundle.ticket_count,
    winnerCount,
  };
  const inputV2 = { ...input, pickSum: bundle.pick_sum ?? 0 };

  if (bundle.legacy_algo || (bundle.algo !== ALGO_V1 && !v2)) {
    notes.push(
      `Эта акция проведена алгоритмом ${bundle.algo}. Он учебный и небезопасный; ` +
        'пересчёт показан только для проверки исторического результата.',
    );
  }

  if (v2) {
    // Сумму цифр участник может сложить сам по открытому списку — поэтому
    // показываем и её, и выведенный множитель.
    const published = bundle.pick_sum ?? 0;
    notes.push(
      `Цифры участников: сумма ${published}, множитель ${multiplierV2(published)}. ` +
        'Сложи цифры в списке участников — получится то же число.',
    );
  }

  const sequences = v2 ? await drawV2(seed, inputV2) : await drawV1(seed, input);
  const computed = sequences.map((sequence, i) => ({ position: i + 1, sequence }));

  const mismatches: number[] = [];
  for (let i = 0; i < Math.max(computed.length, published.length); i++) {
    if (computed[i]?.sequence !== published[i]?.sequence) mismatches.push(i + 1);
  }

  return {
    matches: mismatches.length === 0 && computed.length === published.length && commitOk,
    commitOk,
    computed,
    published,
    mismatches,
    contextHex: bundle.context_hex ?? bytesToHex(v2 ? drawContextV2(inputV2) : drawContext(input)),
    notes,
  };
}

/**
 * Independently recomputes a frozen ticket list's Merkle root from the public
 * participants list, so a participant can confirm the published root really
 * describes the list they can see — and that their own number is in it.
 */
export async function checkTicketList(
  numbers: string[],
  publishedRoot: string,
  /** Цифры участников — у акций v2 они часть замороженного списка. */
  picks?: number[],
): Promise<{ matches: boolean; computedRoot: string }> {
  const computedRoot = picks ? await ticketRootV2(numbers, picks) : await ticketRoot(numbers);
  return { matches: computedRoot === publishedRoot.toLowerCase(), computedRoot };
}

/**
 * Recomputes a legacy v0 draw, for campaigns created with the prototype engine.
 * Kept separate so no v1 code path can accidentally call into it.
 */
export function checkLegacyDraw(
  seed: string,
  prefix: string,
  ticketCount: number,
  winnerCount: number,
): { winners: string[]; seedHash: string } {
  return {
    winners: drawWinnersV0(seed, prefix, ticketCount, winnerCount),
    seedHash: hashHexV0(seed),
  };
}
